// Pure helpers behind the dashboard (frontend/ui/project_dashboard.py), kept free of React so they can be tested.
import type { FolderList, ImageOut, User } from "../api/types";

export const STATUS_COLORS: Record<string, string> = {
  unannotated: "#607D8B", in_progress: "#FFA726", annotated: "#43A047", auto_annotated: "#FF8C00", empty: "#43A047",
};
export const STATUS_LABELS: Record<string, string> = {
  unannotated: "Not annotated", in_progress: "In progress", annotated: "Annotated", auto_annotated: "Needs review",
  empty: "Empty",
};

/**
 * What a folder upload picks up. The desktop also takes .bmp and .tiff; a browser cannot display
 * those and the API has no display copy, so the web picker leaves them out (parity note P10/P11).
 */
export const WEB_IMAGE_SUFFIXES = [".jpg", ".jpeg", ".png", ".webp"];
export const BROWSER_UNDISPLAYABLE = [".bmp", ".tiff", ".tif"];

export const stem = (filename: string) => { const base = filename.split("/").pop() ?? filename; const i = base.lastIndexOf("."); return i > 0 ? base.slice(0, i) : base; };

/** "Total: 3  |  Unannotated: 2  |  Annotated: 1" (_refresh_stats). */
export function statsLine(images: ImageOut[]): string {
  const counts = new Map<string, number>();
  for (const i of images) counts.set(i.status || "unannotated", (counts.get(i.status || "unannotated") ?? 0) + 1);
  return [`Total: ${images.length}`, ...[...counts].map(([k, v]) => `${STATUS_LABELS[k] ?? k}: ${v}`)].join("  |  ");
}

/**
 * Gallery keys (web; the desktop's list widget has them built in): Ctrl+A / ⌘A selects every image the gallery shows,
 * Esc clears the selection. Not while typing in a field, and not while a dialog is open, where the keys mean
 * something else.
 */
export function galleryKey(e: { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean; shiftKey: boolean },
  ctx: { typing: boolean; dialogOpen: boolean }): "select_all" | "clear" | null {
  if (ctx.typing || ctx.dialogOpen || e.altKey) return null;
  if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "a") return "select_all";
  if (e.key === "Escape" && !e.ctrlKey && !e.metaKey && !e.shiftKey) return "clear";
  return null;
}

/** "3 of 120 selected", or "" when nothing is. Counts only images the gallery shows. */
export function selectionLine(selected: ReadonlySet<number>, shown: number[]): string {
  const n = shown.filter((id) => selected.has(id)).length;
  return n ? `${n} of ${shown.length} selected` : "";
}

/** The gallery label under a thumbnail: the stem, and a status pill "Needs review · 3" (word and count, never colour alone). */
export const cardLabel = (i: ImageOut) => ({
  name: stem(i.filename), status: STATUS_LABELS[i.status] ? i.status : "unannotated",
  detail: `${STATUS_LABELS[i.status] ?? i.status} · ${i.annotation_count}`,
});

/** Filename search, case-insensitive, applied without a server round trip (_apply_search). */
export function matchesSearch(i: ImageOut, text: string): boolean {
  const needle = text.trim().toLowerCase();
  return !needle || i.filename.toLowerCase().includes(needle);
}

export interface FolderOption { value: string | null; label: string }
/** Folder selector rows (_refresh_folders): value null = all, "" = no folder. */
export function folderOptions(data: FolderList | null): FolderOption[] {
  const rows: FolderOption[] = [{ value: null, label: "All folders" }];
  for (const r of data?.folders ?? []) rows.push({ value: r.folder, label: `${r.folder}  (${r.image_count})` });
  if (data?.unfoldered) rows.push({ value: "", label: `No folder  (${data.unfoldered})` });
  return rows;
}

/** A selection that points at a folder that no longer exists falls back to "All folders". */
export const keepFolder = (current: string | null, options: FolderOption[]) =>
  options.some((o) => o.value === current) ? current : null;

/**
 * Selection in the gallery, like QAbstractItemView.ExtendedSelection: a plain click selects only that
 * image, Ctrl/Cmd toggles it, Shift selects the run from the last clicked image.
 */
export function clickSelect(
  selected: ReadonlySet<number>, order: number[], anchor: number | null, id: number,
  mods: { ctrl: boolean; shift: boolean },
): { selected: Set<number>; anchor: number } {
  if (mods.shift && anchor !== null && order.includes(anchor)) {
    const [a, b] = [order.indexOf(anchor), order.indexOf(id)].sort((x, y) => x - y);
    const run = order.slice(a, b + 1);
    return { selected: new Set(mods.ctrl ? [...selected, ...run] : run), anchor };
  }
  if (mods.ctrl) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    return { selected: next, anchor: id };
  }
  return { selected: new Set([id]), anchor: id };
}

/** Admin dashboard (admin_dashboard.py). */
/** Admins and the platform role that includes them (core/permissions.py is_org_admin). */
export const isOrgAdmin = (role: string | undefined | null) => role === "admin" || role === "super_admin";
/** Roles that may change data; a viewer is read-only (core/permissions.py). */
export const canEdit = (role: string | undefined | null) => role === "annotator" || isOrgAdmin(role);

export const trialText = (u: User) => (isOrgAdmin(u.role) ? "—" : u.trial_days_remaining == null ? "not started" : `${u.trial_days_remaining}d left`);

export function adminButtons(selected: User | null, me: User | null) {
  if (!selected) return { role: false, roleLabel: "Make Admin", extend: false, resetTrial: false, resetPassword: false };
  const isAdmin = isOrgAdmin(selected.role);
  return {
    role: selected.id !== me?.id && !(selected.role === "super_admin" && me?.role !== "super_admin"),
    roleLabel: isAdmin ? "Make Annotator" : "Make Admin",
    extend: !isAdmin, resetTrial: !isAdmin, resetPassword: true,
  };
}

/** export_dialog._update_test_pct */
export const testPct = (train: number, val: number) => 100 - train - val;

// ── Bulk review of the selected images ───────────────────────────────────────────────────────────
const n = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;

/** What the dry run found, in words: the confirmation question, or why there is nothing to confirm. */
export function bulkReviewPrompt(r: { annotations_marked: number; images_affected: number; annotations_skipped_unlabelled: number; images_not_found: number }):
  { kind: "nothing"; text: string } | { kind: "confirm"; text: string } {
  const notes: string[] = [];
  if (r.annotations_skipped_unlabelled) notes.push(`${n(r.annotations_skipped_unlabelled, "annotation")} with no class will be left unreviewed.`);
  if (r.images_not_found) notes.push(`${n(r.images_not_found, "selected image")} no longer exist${r.images_not_found === 1 ? "s" : ""} and will be skipped.`);
  if (!r.annotations_marked) {
    return { kind: "nothing", text: ["There is nothing to mark: every annotation on the selected images is already reviewed, or has no class yet.", ...notes].join("\n\n") };
  }
  return {
    kind: "confirm",
    text: [`Mark ${n(r.annotations_marked, "annotation")} on ${n(r.images_affected, "image")} as reviewed?`,
      "Reviewed annotations are what training learns from, so only do this for images you have checked. No shape or class is changed.", ...notes].join("\n\n"),
  };
}

// ── Marking the selected images empty ────────────────────────────────────────────────────────────

/** What the mark-empty dry run found, in words: the confirmation question, or why there is nothing to do. */
export function markEmptyPrompt(r: { images_marked: number; annotations_deleted: number; images_skipped_reviewed: number; images_not_found: number }):
  { kind: "nothing"; text: string } | { kind: "confirm"; text: string } {
  const notes: string[] = [];
  if (r.images_skipped_reviewed) notes.push(`${n(r.images_skipped_reviewed, "image")} with reviewed annotations will be left as ${r.images_skipped_reviewed === 1 ? "it is" : "they are"}. Delete those annotations first if the image${r.images_skipped_reviewed === 1 ? " is" : "s are"} really empty.`);
  if (r.images_not_found) notes.push(`${n(r.images_not_found, "selected image")} no longer exist${r.images_not_found === 1 ? "s" : ""} and will be skipped.`);
  if (!r.images_marked) {
    return { kind: "nothing", text: ["There is nothing to mark: the selected images are already marked empty, or have reviewed annotations.", ...notes].join("\n\n") };
  }
  const deleting = r.annotations_deleted
    ? `Their ${n(r.annotations_deleted, "unreviewed annotation")} will be deleted.`
    : "";
  return {
    kind: "confirm",
    text: [`Mark ${n(r.images_marked, "image")} as empty (none of your objects in ${r.images_marked === 1 ? "it" : "them"})?`,
      ["Training uses empty images as background, which teaches the model not to detect anything there.", deleting].filter(Boolean).join(" "),
      ...notes].join("\n\n"),
  };
}
